<template>
  <div>
    <div class="document-container">
      <div class="columns is-desktop">
        <div class="column">
          <div class="document">
            <div class="header">
              <es-header>
                <template #header>
                  <div class="header-title">
                    Create a Location
                  </div>
                </template>
              </es-header>
            </div>
            <es-loader
              v-if="loading"
              style="margin: 6.5rem"
            />
            <es-exception
              v-else-if="loadingError"
              style="padding-bottom: 7rem;"
              exceptionMessage="There was an issue.
              Please try again or contact your administrator."
            />
            <div
              v-else
              class="content"
            >
              <es-form>
                <template #es-form-header>
                  <div class="header-container">
                    <div class="header-elements">
                      <font-awesome-icon :icon="['fas', 'map-marker-alt']"/>
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        Location Details
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          Organization
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <es-select
                              valueTpl="${name}"
                              keyValue="code"
                              v-on:update:values="organization = $event;
                                validateField('organization')"
                              :clearOut="true"
                              :errorMsg="msgOrganization"
                              :hasError="hasErrorOrganization"
                              :setValues="activeOrganizations"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          Location Name
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input"
                              :class="{ 'is-danger': hasErrorLocationName }"
                              @input="validateField('locationName')"
                              type="text"
                              placeholder="Enter Location Name"
                              v-model="locationName"
                            >
                            <span
                              v-if="hasErrorLocationName"
                              class="help is-danger"
                            >
                              {{ msgLocationName }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          Location Code
                          <span class="label-optional has-text-grey-light">
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input"
                              type="text"
                              placeholder="Enter Local Code"
                              v-model="localCode"
                              maxlength="40"
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          Address 1
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input"
                              :class="{ 'is-danger': hasErrorAddress1 }"
                              @input="validateField('address1')"
                              type="text"
                              placeholder="Enter Address 1"
                              v-model="address1"
                            >
                            <span
                              v-if="hasErrorAddress1"
                              class="help is-danger"
                            >
                              {{ msgAddress1 }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          Address 2
                          <span class="label-optional has-text-grey-light">
                            &nbsp;(optional)
                          </span>
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input"
                              type="text"
                              placeholder="Enter Address 2"
                              v-model="address2"
                              maxlength="100"
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          City
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input"
                              :class="{ 'is-danger': hasErrorCity }"
                              @input="validateField('city')"
                              type="text"
                              placeholder="Enter City"
                              v-model="city"
                            >
                            <span
                              v-if="hasErrorCity"
                              class="help is-danger"
                            >
                              {{ msgCity }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          State
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <es-select
                              valueTpl="${description}"
                              keyValue="code"
                              v-on:update:values="stateCode = $event;
                                validateField('state')"
                              :clearOut="true"
                              :errorMsg="msgStateCode"
                              :hasError="hasErrorStateCode"
                              :setValues="activeStates"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          Zip
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                            <input
                              class="input"
                              :class="{ 'is-danger': hasErrorZip }"
                              @input="validateField('zip')"
                              type="text"
                              placeholder="Enter Zip"
                              v-model="zip"
                            >
                            <span
                              v-if="hasErrorZip"
                              class="help is-danger"
                            >
                              {{ msgZip }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </es-form>
              <es-form>
                <template #es-form-header>
                  <div class="header-container">
                    <div class="header-elements">
                      <font-awesome-icon :icon="['fas', 'map-marked-alt']"/>
                    </div>
                    <div class="header-elements">
                      <h6 class="title">
                        More
                      </h6>
                    </div>
                  </div>
                </template>
                <template #es-form>
                  <div class="row removeUnderline">
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          Start Date
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control">
                              <input
                                class="input calendar is-half-width"
                                :class="{ 'is-danger': hasErrorStartDate }"
                                type="date"
                                @input="validateField('startDate')"
                                v-model="startDate"
                              />
                          </div>
                          <span
                            v-if="hasErrorStartDate"
                            class="help is-danger"
                          >
                           {{ msgStartDate }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </es-form>
              <es-form>
                <template #es-form>
                  <div class="field is-grouped flex-justify--end">
                    <div class="control is-flex flex-align--center">
                      <a @click="resetForm">
                        Reset
                      </a>
                    </div>
                    <div class="control">
                      <button
                        class="button is-primary"
                        :disabled="creatingLocation"
                        @click.stop.prevent="validateFormFields"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </template>
              </es-form>
            </div>
          </div>
        </div>
        <div
          v-if="hasModule('LOCID:MATCH:HISTORY:VIEW')
            || hasModule('LOCID:MATCH:RESOLVENM:VIEW')"
          class="column"
        >
          <recent-near-match-summary
            v-if="hasModule('LOCID:MATCH:RESOLVENM:VIEW')"
            class="margin-bottom"
            :matches="nearMatches"
            :loading="loaderNearMatch"
          />
          <recent-location-summary
            v-if="hasModule('LOCID:MATCH:HISTORY:VIEW')"
            :locations="locations"
            :loading="loaderLoacations"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faMapMarkedAlt,
  faMapMarkerAlt,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import RecentLocationSummary from '@/components/RecentLocationSummary.vue';
import RecentNearMatchSummary from '@/components/RecentNearMatchSummary.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import ZipUtils from '@/mixins/ZipUtils';

library.add(
  faAngleDoubleRight,
  faMapMarkedAlt,
  faMapMarkerAlt,
);

export default {
  mixins: [
    BreadcrumbsManager,
    NotificationManager,
    PermissionManager,
    ZipUtils,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsSelect,
    RecentLocationSummary,
    RecentNearMatchSummary,
  },
  data() {
    return {
      address1: null,
      address2: null,
      city: null,
      msgCity: null,
      creatingLocation: false,
      formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
      hasErrorCity: false,
      hasErrorLocationName: false,
      hasErrorOrganization: false,
      hasErrorStartDate: false,
      hasErrorStateCode: false,
      hasErrorAddress1: false,
      hasErrorZip: false,
      loaderLoacations: false,
      loaderNearMatch: false,
      loading: false,
      loadingError: false,
      localCode: null,
      locations: [],
      locationName: null,
      msgLocationName: null,
      nearMatches: [],
      msgOrganization: null,
      organization: null,
      startDate: null,
      msgStartDate: null,
      stateCode: null,
      msgStateCode: null,
      msgAddress1: null,
      zip: null,
      msgZip: null,
    };
  },
  created() {
    if (this.hasModule('LOCID:APP:NAV:VIEW')) { // if can see recenlty added locations
      this.getLocations();
    }
    if (this.hasModule('LOCID:MATCH:RESOLVENM:VIEW')) { // if can see near matches
      this.getNearMatches();
    }
    this.setStartDate();
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'groupList' } },
      { text: 'Create a Location', to: { } },
    ]);
    this.onLoad();
  },
  computed: {
    ...mapGetters('lookups', [
      'activeOrganizations',
      'activeStates',
      'activeSources',
    ]),
    locationSearchParams() {
      const params = {};
      params.pageSize = 5;

      const sorters = {};
      sorters.field = 'createdDate';
      sorters.order = 'DESC';
      const sort = [];
      sort.push(sorters);
      params.sort = JSON.stringify(sort);
      return params;
    },
    matchSearchParams() {
      const params = {};
      params.pageSize = 5;
      params.open = true;

      const sorters = {};
      sorters.field = 'transDate';
      sorters.order = 'DESC';
      const sort = [];
      sort.push(sorters);
      params.sort = JSON.stringify(sort);
      return params;
    },
    onLineSource() {
      if (this.activeSources && this.activeSources.length > 0) {
        let onlineSource = this.activeSources
          .find(el => el.code === 'O' || el.code === 'o');
        if (!onlineSource) {
          onlineSource = this.activeSources
            .find(el => el.isDefault === true);
          return onlineSource ? onlineSource.code : this.activeSources[0].code;
        }
        return onlineSource.code;
      }
      return null;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadOrganizations',
      'loadStates',
      'loadSources',
    ]),
    toggleLoading() {
      this.loaderLoacations = !this.loaderLoacations;
    },
    toggleMatchLoading() {
      this.loaderNearMatch = !this.loaderNearMatch;
    },
    setStartDate() {
      this.startDate = moment().format('YYYY-MM-DD');
    },
    async getLocations() {
      this.toggleLoading();
      try {
        const queryParams = new URLSearchParams();
        Object.keys(this.locationSearchParams).forEach((key) => {
          queryParams.append(key, this.locationSearchParams[key]);
        });
        const res = await axios.get('location-organizations', { params: queryParams });
        this.locations = res.data.data;
      } catch {
        // handle error
      }
      this.toggleLoading();
    },
    async getNearMatches() {
      this.toggleMatchLoading();
      try {
        const queryParams = new URLSearchParams();
        Object.keys(this.matchSearchParams).forEach((key) => {
          queryParams.append(key, this.matchSearchParams[key]);
        });
        const res = await axios.get('search/location/nearmatches', { params: queryParams });
        this.nearMatches = res.data.data;
      } catch {
        // handle error
      }
      this.toggleMatchLoading();
    },
    async createLocation() {
      try {
        this.loading = true;
        this.loadingError = false;
        this.creatingLocation = true;
        const res = await axios.post('location-organizations', ({
          organizationCode: this.organization[0].code,
          localCode: this.localCode,
          sourceCode: this.onLineSource,
          name: this.locationName,
          street1: this.address1,
          street2: this.address2,
          city: this.city,
          stateCode: this.stateCode[0].code,
          zip: this.zip,
          startDate: this.startDate,
        }));
        const { transStatus } = res.data;
        if (transStatus === 'LOCID_NO_MATCHES_NEW_ID_INSERTED') {
          const locationId = res.data.newLocationIdWhenInserted;
          this.$router.push({
            name: 'locationDetails',
            params: { id: locationId, transStatus },
          });
        } else if (transStatus === 'LOCID_MATCHED_VIA_SEARCH') {
          const { matchedLocationId } = res.data;
          this.$router.push({
            name: 'locationDetails',
            params: { id: matchedLocationId, transStatus },
          });
        } else if (transStatus === 'LOCID_NEAR_MATCHED') {
          const matchedLocationId = res.data.requestId;
          this.$router.push({
            name: 'nearMatches',
            params: { nearMatchId: matchedLocationId },
          });
        }
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
        this.creatingLocation = false;
      }
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadOrganizations(),
          this.loadStates(),
          this.loadSources(),
        ]);
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    resetErrors() {
      this.resetNotifications();
      this.hasErrorOrganization = false;
      this.hasErrorLocationName = false;
      this.hasErrorAddress1 = false;
      this.hasErrorCity = false;
      this.hasErrorStateCode = false;
      this.hasErrorZip = false;
      this.hasErrorStartDate = false;
      this.msgOrganization = null;
      this.msgLocationName = null;
      this.msgAddress1 = null;
      this.msgCity = null;
      this.msgStateCode = null;
      this.msgZip = null;
      this.msgStartDate = null;
    },
    resetForm() {
      this.resetErrors();
      this.setStartDate();
      this.address1 = null;
      this.address2 = null;
      this.city = null;
      this.localCode = null;
      this.locationName = null;
      this.organization = null;
      this.stateCode = null;
      this.zip = null;
    },
    resetNotifications() {
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    validateField(field) {
      if (field === 'organization') {
        if (!this.organization || this.organization.length === 0 || this.organization === null) {
          this.hasErrorOrganization = true;
          this.msgOrganization = 'Organization is required.';
        } else {
          this.hasErrorOrganization = false;
          this.msgOrganization = null;
        }
      }
      if (field === 'locationName') {
        if (!this.locationName || this.locationName === '') {
          this.hasErrorLocationName = true;
          this.msgLocationName = 'Location Name is required.';
        } else if (this.locationName.length > 250) {
          this.hasErrorLocationName = true;
          this.msgLocationName = 'Location Name allows a maximum of 250 characters.';
        } else {
          this.hasErrorLocationName = false;
          this.msgLocationName = null;
        }
      }
      if (field === 'address1') {
        if (!this.address1 || this.address1 === '') {
          this.hasErrorAddress1 = true;
          this.msgAddress1 = 'Address 1 is required.';
        } else if (this.address1.length > 100) {
          this.hasErrorAddress1 = true;
          this.msgAddress1 = 'Address 1 allows a maximum of 100 characters.';
        } else {
          this.hasErrorAddress1 = false;
          this.msgAddress1 = null;
        }
      }
      if (field === 'city') {
        if (!this.city || this.city === '') {
          this.hasErrorCity = true;
          this.msgCity = 'City is required.';
        } else if (this.city.length > 40) {
          this.hasErrorCity = true;
          this.msgCity = 'City allows a maximum of 40 characters.';
        } else {
          this.hasErrorCity = false;
          this.msgCity = null;
        }
      }
      if (field === 'state') {
        if (!this.stateCode || this.stateCode.length === 0 || this.stateCode === null) {
          this.hasErrorStateCode = true;
          this.msgStateCode = 'State is required.';
        } else {
          this.hasErrorStateCode = false;
          this.msgStateCode = null;
        }
      }
      if (field === 'zip') {
        if (!this.zip || this.zip === '') {
          this.hasErrorZip = true;
          this.msgZip = 'Zip is required.';
        } else if (this.zip.length > 10) {
          this.hasErrorZip = true;
          this.msgZip = 'Zip allows a maximum of 10 characters.';
        } else {
          this.hasErrorZip = false;
          this.msgZip = null;
        }
      }
      if (field === 'startDate') {
        const today = new Date().getTime();
        if (!this.startDate || this.startDate.length === 0) {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date is required.';
        } else if (this.startDate && moment(this.startDate).isAfter(today)) {
          this.hasErrorStartDate = true;
          this.msgStartDate = 'Start Date cannot be a future date.';
        } else {
          this.hasErrorStartDate = false;
          this.msgStartDate = null;
        }
      }
    },
    validateFormFields() {
      this.validateField('organization');
      this.validateField('locationName');
      this.validateField('address1');
      this.validateField('city');
      this.validateField('state');
      this.validateField('zip');
      this.validateField('startDate');

      const err = this.hasErrorOrganization
        || this.hasErrorLocationName
        || this.hasErrorAddress1
        || this.hasErrorCity
        || this.hasErrorStateCode
        || this.hasErrorZip
        || this.hasErrorStartDate;
      if (err) {
        this.showNotification('danger', [this.formErrorMsg]);
      } else {
        this.resetNotifications();
        this.createLocation();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .margin-bottom
    margin-bottom: 1rem
  .columns
    margin-left: -0.5rem
    margin-right: -0.5rem
    margin-top: -0.5rem
    &:last-child
      margin-bottom: -0.5rem
  .column
    padding: 0.5rem
</style>
